import { t } from 'ember-intl';
import { and, or } from 'tio-ui/utilities';
import Button from 'tio-ui/components/button';
import type { TOC } from '@ember/component/template-only';
import type { PreviewCounts } from 'tio-data/data/resources/eligibility-file-job-log.type';

interface DetailsBoxSignature {
  Args: {
    title: string;
    /** Also the `data-test` value, so a test can address one card by name, not position. */
    category: string;
    /** Missing whenever the job log's preview stage has not run. */
    preview: number | undefined;
    processed: number | undefined;
    previewCounts: PreviewCounts | null | undefined;
    canViewDetails?: boolean;
    onViewDetails?: (() => void) | undefined;
    canViewTopChanges?: boolean;
    onViewTopChanges?: (() => void) | undefined;
  };
}

// '' hides the line; without it an unknowable denominator renders "NaN%".
function percentOfTotal(
  preview: number | undefined,
  allPreviewCounts: PreviewCounts | null | undefined
): string {
  if (!allPreviewCounts || !preview) return '';
  const { unchanged, updated, deactivated } = allPreviewCounts;
  if (unchanged === undefined) return ''; // no `unchanged` means no knowable population size
  const existing = unchanged + updated + deactivated;
  if (existing === 0) return '';
  const pct = (preview / existing) * 100;
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`;
}

function percentComplete(preview: number | undefined, processed: number | undefined): string {
  if (!preview) return '0%';
  return `${Math.floor(((processed ?? 0) / preview) * 100)}%`;
}

/** Each drill-down button renders only when given both its `canView*` flag and its handler. */
const DetailsBox: TOC<DetailsBoxSignature> = <template>
  <div
    class="rounded-md text-center my-2 border tio-border-default tio-bg-surface"
    data-test-eligibility-details-box={{@category}}
  >
    <div class="py-4 font-semibold tio-fg-brand">
      {{@title}}
    </div>
    {{#if (percentOfTotal @preview @previewCounts)}}
      <div class="tio-text-lg-bold tio-fg-primary" data-test-eligibility-details-box-percent>
        {{percentOfTotal @preview @previewCounts}}
        <span class="tio-text-xs-normal tio-fg-muted">{{t "eligibility_files.of_total"}}</span>
      </div>
    {{/if}}
    <dl>
      <div class="grid grid-cols-2 p-4 tio-fg-primary">
        <dt class="text-left tio-fg-secondary">{{t "common.eligibility_file_upload.preview"}}</dt>
        <dd>{{@preview}}</dd>
        <dt class="text-left tio-fg-secondary">{{t "common.eligibility_file_upload.processed"}}</dt>
        <dd>{{@processed}}</dd>
        {{#if @preview}}
          <dt class="text-left tio-fg-secondary">{{t "eligibility_files.completion"}}</dt>
          <dd>{{percentComplete @preview @processed}}</dd>
        {{/if}}
      </div>
    </dl>
    {{#if (or (and @canViewDetails @onViewDetails) (and @canViewTopChanges @onViewTopChanges))}}
      <div class="tio-row-center gap-3 pb-3">
        {{#if (and @canViewDetails @onViewDetails)}}
          <Button
            @appearance="minimal"
            @size="sm"
            class="hover:underline"
            {{! @glint-expect-error: guarded above; glint cannot narrow through a helper }}
            {{on "click" @onViewDetails}}
          >
            {{t "eligibility_files.view_details"}}
          </Button>
        {{/if}}
        {{#if (and @canViewDetails @onViewDetails @canViewTopChanges @onViewTopChanges)}}
          <span class="tio-fg-faint">|</span>
        {{/if}}
        {{#if (and @canViewTopChanges @onViewTopChanges)}}
          <Button
            @appearance="minimal"
            @size="sm"
            class="hover:underline"
            {{! @glint-expect-error: guarded above; glint cannot narrow through a helper }}
            {{on "click" @onViewTopChanges}}
          >
            {{t "eligibility_files.top_changes"}}
          </Button>
        {{/if}}
      </div>
    {{/if}}
  </div>
</template>;

export default DetailsBox;
